import {
  DEFAULT_FORM_SETTINGS,
  FormDateParts,
  FormField,
  FormFieldBox,
  FormFieldType,
  FormTemplateSettings,
} from "../../../interfaces/DTO/forms/formTemplate";

// Fields' boxes are stored in PDF points with the origin at the page's bottom-left corner (as the server draws
// them); the editor shows the page scaled to its width, with the origin at the top-left. `scale` = screen pixels
// per point.

export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const round = (n: number) => Math.round(n * 2) / 2; // half a point is precise enough

export function boxToScreen(box: FormFieldBox, pageHeight: number, scale: number): ScreenRect {
  return {
    left: box.x * scale,
    top: (pageHeight - box.y - box.h) * scale,
    width: box.w * scale,
    height: box.h * scale,
  };
}

export function screenToBox(rect: ScreenRect, pageHeight: number, scale: number): FormFieldBox {
  const w = Math.max(rect.width / scale, 2);
  const h = Math.max(rect.height / scale, 2);
  return {
    x: round(rect.left / scale),
    y: round(pageHeight - rect.top / scale - h),
    w: round(w),
    h: round(h),
  };
}

/** A box kept inside the page */
export function clampBox(box: FormFieldBox, pageWidth: number, pageHeight: number): FormFieldBox {
  const w = Math.min(box.w, pageWidth);
  const h = Math.min(box.h, pageHeight);
  return {
    x: round(Math.min(Math.max(box.x, 0), pageWidth - w)),
    y: round(Math.min(Math.max(box.y, 0), pageHeight - h)),
    w: round(w),
    h: round(h),
  };
}

/** A key not used yet: field_1, field_2, ... */
export function newFieldKey(fields: FormField[], base = "field"): string {
  const used = new Set(fields.map((f) => f.key));
  let n = fields.length + 1;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

// ---------- date format: day-month-year, a separator and a 4 / 2 digit year (the server's strftime format) ----------

export const DEFAULT_DATE_FORMAT = "%d-%m-%Y";
export type DateSeparator = "-" | "/" | "." | "";

export function dateFormatParts(format?: string | null): { separator: DateSeparator; shortYear: boolean } {
  const f = format || DEFAULT_DATE_FORMAT;
  const sep = f.replace(/%[dmYy]/g, "").charAt(0);
  return { separator: (["-", "/", "."].includes(sep) ? sep : "") as DateSeparator, shortYear: f.includes("%y") };
}

export function buildDateFormat(separator: DateSeparator, shortYear: boolean): string {
  return ["%d", "%m", shortYear ? "%y" : "%Y"].join(separator);
}

/**
 * An ISO date (YYYY-MM-DD) in the field's format - as the server draws it: in comb cells the separators only when
 * there is a cell for them. Anything else as is.
 */
export function formatDateValue(value: string, format?: string | null, cells?: number | null): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!iso) return value;
  const [, y, m, d] = iso;
  const text = (format || DEFAULT_DATE_FORMAT).replace("%d", d).replace("%m", m).replace("%Y", y).replace("%y", y.slice(2));
  return cells && text.length > cells ? text.replace(/\D/g, "") : text;
}

/**
 * A typed date as ISO (YYYY-MM-DD) - like the server's as_date: ISO, or day-month-year with / . - or a space
 * ("17/05/1980", "17-05-80", "17051980"). null if it is not a date.
 */
export function parseDateValue(value: string): string | null {
  const text = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(T.*)?$/.exec(text);
  const dmy = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{4}|\d{2})$/.exec(text) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(text);
  let y: number, m: number, d: number;
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (dmy) {
    [d, m, y] = [Number(dmy[1]), Number(dmy[2]), Number(dmy[3])];
    if (dmy[3].length === 2) y += y < 69 ? 2000 : 1900; // as Python's %y
  } else return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Data paths that hold a date - a field mapped to one is a date field */
export const isDateSource = (source?: string | null) => !!source && /(^today$|_date$)/.test(source);

/** Day / month / year boxes splitting a date box in three - left to right as the date is written (DD-MM-YYYY), the year last */
export function splitDateBox(box: FormFieldBox): FormDateParts {
  const third = box.w / 3;
  return {
    day: { x: round(box.x), y: box.y, w: round(third), h: box.h },
    month: { x: round(box.x + third), y: box.y, w: round(third), h: box.h },
    year: { x: round(box.x + 2 * third), y: box.y, w: round(third), h: box.h },
  };
}

/** The boxes a field covers on the page: one per row for a repeated field, the parts of a split date */
export function fieldBoxes(field: FormField): FormFieldBox[] {
  const base = field.type === FormFieldType.DATE && field.date_parts
    ? [field.date_parts.day, field.date_parts.month, field.date_parts.year]
    : [field.box];
  if (!field.repeat) return base;
  const rows: FormFieldBox[] = [];
  for (let i = 0; i < field.repeat.rows; i += 1) {
    for (const b of base) rows.push({ ...b, y: b.y - i * field.repeat.row_pitch });
  }
  return rows;
}

/** Moving a field moves its date parts with it */
export function moveField(field: FormField, box: FormFieldBox): FormField {
  const dx = box.x - field.box.x;
  const dy = box.y - field.box.y;
  const shift = (b: FormFieldBox): FormFieldBox => ({ ...b, x: round(b.x + dx), y: round(b.y + dy) });
  return {
    ...field,
    box,
    date_parts: field.date_parts
      ? { ...field.date_parts, day: shift(field.date_parts.day), month: shift(field.date_parts.month), year: shift(field.date_parts.year) }
      : field.date_parts,
  };
}

/** A problem of one field - `index` is its place in the fields list (to jump to it) */
export interface FieldProblem {
  index: number;
  name: string; // the field's label, else its key
  message: string;
}

/** Problems that would make the server reject the fields - shown before saving, each pointing at its field */
export function fieldProblems(fields: FormField[], pageCount: number): FieldProblem[] {
  const problems: FieldProblem[] = [];
  const seen = new Set<string>();
  fields.forEach((f, index) => {
    const add = (message: string) => problems.push({ index, name: f.label || f.key, message });
    if (seen.has(f.key)) add(`מפתח כפול (${f.key})`);
    seen.add(f.key);
    if (f.page > pageCount) add(`עמוד ${f.page} לא קיים`);
    if (f.type === FormFieldType.CHOICE && !f.choice_value) add("חסר \"הערך שמסמן את התיבה\"");
    if (f.type === FormFieldType.CHOICE && f.choice_value) {
      const twin = fields.findIndex((o, j) => j !== index && o.type === FormFieldType.CHOICE
        && o.choice_value === f.choice_value && choiceGroup(o) === choiceGroup(f) && o.page === f.page);
      if (twin >= 0 && twin < index) add(`הערך "${f.choice_value}" כבר מסומן בתיבה אחרת בקבוצה`);
    }
    if (f.type === FormFieldType.SIGNATURE && !f.signer) add("חסר מי חותם");
    if (f.cells && !(f.type === FormFieldType.TEXT || (f.type === FormFieldType.DATE && !f.date_parts))) {
      add("חלוקה לתאים רק לשדה טקסט או תאריך בתיבה אחת");
    }
    const indexed = f.key.includes("{i}") || (f.source ?? "").includes("{i}");
    if (f.repeat && !f.key.includes("{i}")) add("שדה חוזר צריך {i} במפתח");
    if (!f.repeat && indexed) add("{i} מותר רק בשדה חוזר");
  });
  return problems;
}

/**
 * A repeated field becomes one field per row - "{i}" in its key / source is the row index, row i is drawn
 * row_pitch points lower (the same as the server's form_renderer.expand_fields).
 */
export function expandFields(fields: FormField[]): FormField[] {
  const out: FormField[] = [];
  for (const f of fields) {
    if (!f.repeat) {
      out.push(f);
      continue;
    }
    for (let i = 0; i < f.repeat.rows; i += 1) {
      const shift = i * f.repeat.row_pitch;
      const down = (b: FormFieldBox): FormFieldBox => ({ ...b, y: b.y - shift });
      out.push({
        ...f,
        key: f.key.split("{i}").join(String(i)),
        source: f.source ? f.source.split("{i}").join(String(i)) : f.source,
        choice_group: f.choice_group ? `${f.choice_group}.${i}` : f.choice_group, // a group per row
        box: down(f.box),
        date_parts: f.date_parts
          ? { ...f.date_parts, day: down(f.date_parts.day), month: down(f.date_parts.month), year: down(f.date_parts.year) }
          : f.date_parts,
        repeat: null,
      });
    }
  }
  return out;
}

/**
 * The choice boxes that belong together (marking one clears the others): the same source, or - without a
 * source - the same group name, else the same key up to its last dot (member.marital.married / member.marital.single).
 */
export function choiceGroup(field: FormField): string {
  if (field.source) return `source:${field.source}`;
  if (field.choice_group) return `group:${field.choice_group}`;
  const dot = field.key.lastIndexOf(".");
  return `key:${dot > 0 ? field.key.slice(0, dot) : field.key}`;
}

/** The standard checkbox size of a page, in points (a form's checkboxes are usually ~10 x 10) */
export const DEFAULT_CHECKBOX_SIZE = 10;

/**
 * A drawn box that is a checkbox: small (each side between half and twice the standard size) and about
 * square (the long side at most 1.6 times the short one).
 */
export function looksLikeCheckbox(box: FormFieldBox, size: number): boolean {
  const short = Math.min(box.w, box.h);
  const long = Math.max(box.w, box.h);
  return short >= size * 0.5 && long <= size * 2 && long / short <= 1.6;
}

/** A square of the given size around the box's centre */
export function squareBox(box: FormFieldBox, size: number): FormFieldBox {
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  return { x: round(cx - size / 2), y: round(cy - size / 2), w: size, h: size };
}

/** The usual checkbox size of the fields (the most common side of the checkbox / choice boxes), or the default */
export function typicalCheckboxSize(fields: FormField[]): number {
  const sides = fields
    .filter((f) => f.type === FormFieldType.CHECKBOX || f.type === FormFieldType.CHOICE)
    .map((f) => round(Math.min(f.box.w, f.box.h)));
  if (!sides.length) return DEFAULT_CHECKBOX_SIZE;
  const counts = new Map<number, number>();
  sides.forEach((s) => counts.set(s, (counts.get(s) ?? 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
}

/** The form's settings with the defaults for what a (older) version does not have */
export function withDefaults(settings?: Partial<FormTemplateSettings> | null): FormTemplateSettings {
  return { ...DEFAULT_FORM_SETTINGS, ...(settings ?? {}) };
}

const isTextBox = (f: FormField) =>
  f.type === FormFieldType.TEXT || (f.type === FormFieldType.DATE && !f.date_parts);
const isCheckBox = (f: FormField) => f.type === FormFieldType.CHECKBOX || f.type === FormFieldType.CHOICE;
const same = (a: number, b: number) => Math.abs(a - b) < 0.01;

/**
 * New default sizes applied to the boxes that follow the defaults: a text box whose height is the old default
 * text height gets the new one, a checkbox / choice square of the old checkbox size gets the new size (around
 * its centre). A box sized by hand is an override - kept as it is.
 */
export function applyDefaultSizes(
  fields: FormField[],
  before: FormTemplateSettings,
  after: FormTemplateSettings,
): FormField[] {
  return fields.map((f) => {
    if (isTextBox(f) && !same(before.text_height, after.text_height) && same(f.box.h, before.text_height)) {
      const cy = f.box.y + f.box.h / 2;
      return { ...f, box: { ...f.box, y: round(cy - after.text_height / 2), h: after.text_height } };
    }
    if (isCheckBox(f) && !same(before.checkbox_size, after.checkbox_size)
        && same(f.box.w, before.checkbox_size) && same(f.box.h, before.checkbox_size)) {
      return { ...f, box: squareBox(f.box, after.checkbox_size) };
    }
    return f;
  });
}

/** A new text box: the drawn width, the form's text height (around the drawn box's centre) */
export function textBox(box: FormFieldBox, height: number): FormFieldBox {
  const cy = box.y + box.h / 2;
  return { ...box, y: round(cy - height / 2), h: height };
}
